import axios, { AxiosInstance, AxiosStatic } from 'axios'
import * as interceptorsModules from './interceptors'
import { IAxiosCreateInstance, IMapInterceptor } from '~/interfaces'

const { REACT_APP_API_VSC_URL: apiBaseUrl } = process.env

const defaultAxios = {
  defaultInstance: axios,
  cleanInstance: true,
  request: [],
  response: ['RefreshToken']
}

const setInterceptor = (
  isGlobal: boolean,
  activeInterceptorsRef: Record<string, any>,
  instance: AxiosInstance | AxiosStatic,
  type: string,
  interceptorName: string | string[],
  ...interceptorsNames: string[]
) => {
  const fetchInterceptors: IMapInterceptor[] | undefined = (
    Array.isArray(interceptorName)
      ? [...interceptorName, ...interceptorsNames]
      : [interceptorName, ...interceptorsNames]
  )
    .map((interceptorName: string) => {
      const interceptor = interceptorsModules[interceptorName]
      return {
        name: interceptorName,
        method: interceptor,
        isGlobal: isGlobal
      } as IMapInterceptor
    })
    .filter(x => x.method)
  fetchInterceptors?.forEach((interceptor: IMapInterceptor) => {
    const { name, method, ...rest } = interceptor
    const isDupe = Object.keys(activeInterceptorsRef[type].activeInterceptors).find(interceptor => interceptor === name)
    const assignInterceptor = () => {
      if (isDupe) {
        return activeInterceptorsRef[type].activeInterceptors[name]
      } else {
        const params = Array.isArray(method) ? ([method[0], method[1]] as const) : ([method] as const)
        const useInterceptor = instance.interceptors[type].use(...params)
        activeInterceptorsRef[type].activeInterceptors[name] = {
          ...rest,
          interceptor: useInterceptor
        }
        return activeInterceptorsRef[type].activeInterceptors[name]
      }
    }
    const { isGlobal } = assignInterceptor()
    if (!isGlobal) {
      const removeAfter = response => {
        removeInterceptor(true, activeInterceptorsRef, instance, type, name)
        instance.interceptors.response.eject(remove)
        return response
      }
      const remove = instance.interceptors.response.use(removeAfter, removeAfter)
    }
  })
}

const removeInterceptor = (
  globalRemove: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeInterceptorsRef: Record<string, any>,
  instance: AxiosInstance | AxiosStatic,
  type: string,
  interceptorName: string | string[],
  ...interceptorsNames: string[]
): void => {
  const modules = Array.isArray(interceptorName)
    ? [...interceptorName, ...interceptorsNames]
    : [interceptorName, ...interceptorsNames]
  const activeInterceptors = { ...activeInterceptorsRef[type].activeInterceptors }
  const mappedInterceptors = {}
  const resetAfterResponse = interceptorKey => {
    instance.interceptors[type].eject(activeInterceptors[interceptorKey].interceptor)
    const resetCallback = config => {
      setInterceptor(activeInterceptors[interceptorKey].isGlobal, activeInterceptorsRef, instance, type, interceptorKey)
      instance.interceptors.response.eject(reset)
      return config
    }
    const reset = instance.interceptors.response.use(resetCallback, resetCallback)
  }
  Object.keys(activeInterceptors).forEach((interceptorKey: string) =>
    modules.includes(interceptorKey)
      ? globalRemove
        ? instance.interceptors[type].eject(activeInterceptors[interceptorKey].interceptor)
        : resetAfterResponse(interceptorKey)
      : Object.assign(mappedInterceptors[interceptorKey], activeInterceptors[interceptorKey])
  )
  activeInterceptorsRef[type].activeInterceptors = { ...mappedInterceptors }
}

const CreateAxiosInstance = (params: IAxiosCreateInstance = {}) => {
  const { request, response, cleanInstance, defaultInstance, baseUrl } = params
  const instance = defaultInstance ?? axios.create()
  instance.defaults.baseURL = baseUrl ?? apiBaseUrl
  const options = {
    axios: instance,
    setAxiosAuth: (token: string): void => {
      instance.defaults.headers.common.Authorization = `Bearer ${token}`
    },
    setCustomHeader: (name: string, value: string): void => {
      instance.defaults.headers.common[name] = value
    },
    request: {
      activeInterceptors: {},
      /** Only use this setter inside a function, for global interceptor use the CreateAxiosInstance constructor
       * example:
       *  const { axios } = CreateAxiosInstance({
       *  request:['interceptor1','interceptor2'],
       *  response:['doMyStuff','interceptor3']
       * })
       */
      setRequestInterceptors: (interceptorName: string | string[], ...interceptorsNames: string[]): void =>
        setInterceptor(false, options, instance, 'request', interceptorName, ...interceptorsNames),
      removeRequestInterceptors: (interceptorName: string | string[], ...interceptorsNames: string[]): void =>
        removeInterceptor(false, options, instance, 'request', interceptorName, ...interceptorsNames),
      globalRemoveRequestInterceptors: (interceptorName: string | string[], ...interceptorsNames: string[]): void =>
        removeInterceptor(true, options, instance, 'request', interceptorName, ...interceptorsNames)
    },
    response: {
      activeInterceptors: {},
      /** Only use this setter inside a function, for global interceptor use the CreateAxiosInstance constructor
       * example:
       *  const { axios } = CreateAxiosInstance({
       *  request:['interceptor1','interceptor2'],
       *  response:['doMyStuff','interceptor3']
       * })
       */
      setResponseInterceptors: (interceptorName: string | string[], ...interceptorsNames: string[]): void =>
        setInterceptor(false, options, instance, 'response', interceptorName, ...interceptorsNames),
      removeResponseInterceptors: (interceptorName: string | string[], ...interceptorsNames: string[]): void =>
        removeInterceptor(false, options, instance, 'response', interceptorName, ...interceptorsNames),
      globalRemoveResponseInterceptors: (interceptorName: string | string[], ...interceptorsNames: string[]): void =>
        removeInterceptor(true, options, instance, 'response', interceptorName, ...interceptorsNames)
    }
  }
  request && setInterceptor(true, options, instance, 'request', request)
  response && setInterceptor(true, options, instance, 'response', response)
  if (!cleanInstance) {
    instance.defaults.headers.common = axios.defaults.headers.common
    setInterceptor(true, options, instance, 'request', defaultAxios.request)
    setInterceptor(true, options, instance, 'response', defaultAxios.response)
  }
  return options
}

const AxiosConfig = CreateAxiosInstance(defaultAxios)

export * from 'axios'
export { axios, AxiosConfig, CreateAxiosInstance }
